/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes, createContext, useMemo } from 'preact/compat';
import { deepmerge, Render } from '@adobe-commerce/elsie/lib';
import { IntlProvider, getDefinitionByLanguage, Lang } from '@adobe-commerce/elsie/i18n';

import '@adobe-commerce/elsie/components/UIProvider/UIProvider.css';
import '@adobe-commerce/elsie/components/UIProvider/normalize.css';

export const UIContext = createContext<{
  locale: string;
}>({
  locale: 'en-US',
});

type LangDefinitions = { [key: string]: any };

export interface UIProviderProps extends HTMLAttributes<HTMLDivElement> {
  lang?: Lang; // ... list of other supported languages
  langDefinitions?: LangDefinitions;
}

export const UIProvider: FunctionComponent<UIProviderProps> = ({
  lang = 'en_US',
  langDefinitions = {},
  children,
}) => {
  const definitions = useMemo(() => {
    // Merge default definition with selected definition
    const selectedDefinitions: LangDefinitions = deepmerge(
      langDefinitions.default,
      langDefinitions[lang] ?? {}
    );

    // Merge SDK default definitions with custom definitions
    return deepmerge(
      getDefinitionByLanguage(lang), // SDK default definitions
      selectedDefinitions // Custom definitions
    );
  }, [lang, langDefinitions]);

  const locale = lang.replace('_', '-');

  return (
    <UIContext.Provider value={{ locale }}>
      <IntlProvider definition={definitions}>
        <>{children}</>
      </IntlProvider>
    </UIContext.Provider>
  );
};

export const provider = new Render(<UIProvider />);